import * as fs from "node:fs";

import {
  CliError,
  EXIT_INPUT,
  EXIT_OK,
  EXIT_RUNTIME,
  type CommandOutput,
  scriptJsonPath,
} from "../../common.js";
import { requireScriptDocument } from "../../domain/script/dispatch.js";
import { isDict, type Dict, strOf } from "../../domain/script/shared.js";
import {
  remoteScriptOutputStoreFromEnv,
  ScriptOutputApiError,
  type ScriptSnapshot,
  type ScriptV2OutputStore,
} from "../../infra/script-output-api.js";
import { syncEpisodeCount } from "../../infra/script-output-store.js";
import {
  assertLocalFileFingerprint,
  assertScriptUnchanged,
  atomicWriteJson,
  localFileFingerprint,
  readWorkingCopyMetadata,
  semanticScriptHash,
  workingCopyPaths,
  writeWorkingCopyMetadata,
  type WorkingCopyMetadata,
} from "../../infra/script-working-copy.js";
import { apiErrorToCli, validateScriptReport } from "./session.js";

const MAX_PUBLISH_ATTEMPTS = 3;

type ValidationPayload = {
  status: "valid";
  summary: Dict;
};

type ManagedPublishResult = {
  revision: number;
  patchedFromRevision: number;
  attempts: number;
  recovered: boolean;
};

function configuredProjectGroupNo(opts: Dict): string {
  return strOf(opts["project_group_no"] || process.env.SANDBOX_PROJECT_GROUP_NO).trim();
}

function remoteClient(projectGroupNo: string | null): ScriptV2OutputStore {
  try {
    return remoteScriptOutputStoreFromEnv(projectGroupNo);
  } catch (error) {
    if (error instanceof ScriptOutputApiError) {
      throw apiErrorToCli("SCRIPT API BLOCKED: gateway not configured", error);
    }
    throw error;
  }
}

function clientForMetadata(opts: Dict, metadata: WorkingCopyMetadata): ScriptV2OutputStore {
  const configuredProject = configuredProjectGroupNo(opts);
  if (configuredProject && configuredProject !== metadata.projectGroupNo) {
    throw remoteMismatch(metadata, `configured projectGroupNo=${configuredProject}`);
  }
  const client = remoteClient(metadata.projectGroupNo);
  if (client.gatewayBase !== metadata.apiBaseUrl) {
    throw remoteMismatch(metadata, `configured apiBaseUrl=${client.gatewayBase}`);
  }
  return client;
}

function remoteMismatch(metadata: WorkingCopyMetadata, received: string): CliError {
  return new CliError("The configured remote does not match this working copy.", {
           exitCode: EXIT_INPUT,
           errorCode: "WORKING_COPY_REMOTE_MISMATCH",
           hint: "Restore the checkout environment for this working copy, or checkout another file.",
           data: { expected: [`projectGroupNo=${metadata.projectGroupNo}`, `apiBaseUrl=${metadata.apiBaseUrl}`], actual: [received] },
         });
}

function readLocalScript(scriptPath: string): Dict {
  let value: unknown;
  try {
    value = JSON.parse(fs.readFileSync(scriptPath, "utf-8"));
  } catch (error) {
    throw new CliError("The local script is missing or invalid JSON.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_JSON_INVALID",
            hint: "Fix the local script JSON before continuing.",
            data: { actual: [scriptPath, error instanceof Error ? error.message : String(error)] },
          });
  }
  if (!isDict(value)) {
    throw new CliError("The script root must be a JSON object.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_ROOT_INVALID",
            hint: "Replace the file with a valid Script v3 JSON object.",
            data: { actual: [scriptPath, Array.isArray(value) ? "array" : typeof value] },
          });
  }
  return value;
}

function isRevisionConflict(error: ScriptOutputApiError): boolean {
  return String(error.code) === "9002" || error.message.includes("版本冲突");
}

function isAmbiguousResponse(error: ScriptOutputApiError): boolean {
  const malformedSuccess = error.status !== null
    && error.status >= 200
    && error.status < 300
    && error.code === null;
  return error.status === null || error.status >= 500 || malformedSuccess;
}

function snapshotRevision(snapshot: ScriptSnapshot): number {
  const revision = Number(snapshot.revision);
  if (!Number.isInteger(revision) || revision < 0) {
    throw new CliError("The gateway returned an invalid snapshot revision.", {
            exitCode: EXIT_RUNTIME,
            errorCode: "PUBLISH_OUTCOME_UNKNOWN",
            hint: "Inspect the remote script before retrying.",
            data: { actual: [JSON.stringify(snapshot)] },
          });
  }
  return revision;
}

function outcomeUnknown(baseRevision: number, received: string[]): CliError {
  return new CliError("The publish outcome could not be confirmed after retrying.", {
           exitCode: EXIT_RUNTIME,
           errorCode: "PUBLISH_OUTCOME_UNKNOWN",
           hint: "Run publish again; it will inspect the latest snapshot before sending another patch.",
           data: { actual: [...received, `baseRevision=${baseRevision}`] },
         });
}

async function publishManagedLocalWins(
  client: ScriptV2OutputStore,
  scriptPath: string,
  script: Dict,
  scriptHash: string,
  metadata: WorkingCopyMetadata,
  validation: ValidationPayload,
): Promise<ManagedPublishResult> {
  const failures: string[] = [];
  for (let attempt = 1; attempt <= MAX_PUBLISH_ATTEMPTS; attempt += 1) {
    let snapshot: ScriptSnapshot | null;
    try {
      snapshot = await client.getSnapshot();
    } catch (error) {
      if (error instanceof ScriptOutputApiError && isAmbiguousResponse(error)) {
        failures.push(`snapshot attempt ${attempt}: ${error.message}`);
        continue;
      }
      if (error instanceof ScriptOutputApiError) throw apiErrorToCli("PUBLISH BLOCKED: snapshot query failed", error);
      throw error;
    }
    if (snapshot === null || !isDict(snapshot.script)) {
      throw new CliError("The managed working copy no longer has a remote script.", {
              exitCode: EXIT_INPUT,
              errorCode: "REMOTE_SCRIPT_NOT_FOUND",
              hint: "Publish a new unmanaged file to create the remote script, or restore the remote project.",
              data: { actual: [`projectGroupNo=${client.projectGroupNo}`] },
            });
    }
    const baseRevision = snapshotRevision(snapshot);
    if (semanticScriptHash(snapshot.script) === scriptHash && snapshot.validationStatus === "valid") {
      return {
        revision: baseRevision,
        patchedFromRevision: baseRevision,
        attempts: attempt,
        recovered: baseRevision !== metadata.baseRevision,
      };
    }

    assertScriptUnchanged(scriptPath, scriptHash);
    try {
      const response = await client.publishPatch({
        baseRevision,
        old: snapshot.script,
        edited: script,
        validation,
      });
      const revision = isDict(response) ? Number(response["revision"]) : Number.NaN;
      if (Number.isInteger(revision) && revision > baseRevision) {
        return { revision, patchedFromRevision: baseRevision, attempts: attempt, recovered: attempt > 1 };
      }
      failures.push(`patch attempt ${attempt}: invalid response ${JSON.stringify(response)}`);
    } catch (error) {
      if (error instanceof ScriptOutputApiError && (isRevisionConflict(error) || isAmbiguousResponse(error))) {
        failures.push(`patch attempt ${attempt}: ${error.message}`);
        continue;
      }
      if (error instanceof ScriptOutputApiError) throw apiErrorToCli("PUBLISH BLOCKED: patch failed", error);
      throw error;
    }
  }
  throw outcomeUnknown(metadata.baseRevision, failures);
}

async function publishInitial(
  client: ScriptV2OutputStore,
  scriptPath: string,
  script: Dict,
  scriptHash: string,
  validation: ValidationPayload,
): Promise<ManagedPublishResult> {
  const failures: string[] = [];
  for (let attempt = 1; attempt <= MAX_PUBLISH_ATTEMPTS; attempt += 1) {
    let snapshot: ScriptSnapshot | null;
    try {
      snapshot = await client.getSnapshot();
    } catch (error) {
      if (error instanceof ScriptOutputApiError && isAmbiguousResponse(error)) {
        failures.push(`snapshot attempt ${attempt}: ${error.message}`);
        continue;
      }
      if (error instanceof ScriptOutputApiError) throw apiErrorToCli("PUBLISH BLOCKED: snapshot query failed", error);
      throw error;
    }

    if (snapshot !== null) {
      const revision = snapshotRevision(snapshot);
      if (isDict(snapshot.script)
        && semanticScriptHash(snapshot.script) === scriptHash
        && snapshot.validationStatus === "valid") {
        return { revision, patchedFromRevision: 0, attempts: attempt, recovered: true };
      }
      throw new CliError("An unmanaged local script cannot replace an existing remote script.", {
              exitCode: EXIT_INPUT,
              errorCode: "REMOTE_SCRIPT_EXISTS",
              hint: "Checkout the remote script, apply the local changes to that managed working copy, then publish.",
              data: { actual: [`projectGroupNo=${client.projectGroupNo}`, `revision=${revision}`] },
            });
    }

    assertScriptUnchanged(scriptPath, scriptHash);
    try {
      const response = await client.publishPatch({
        baseRevision: 0,
        old: {},
        edited: script,
        validation,
      });
      const revision = isDict(response) ? Number(response["revision"]) : Number.NaN;
      if (Number.isInteger(revision) && revision > 0) {
        return { revision, patchedFromRevision: 0, attempts: attempt, recovered: attempt > 1 };
      }
      failures.push(`patch attempt ${attempt}: invalid response ${JSON.stringify(response)}`);
    } catch (error) {
      if (error instanceof ScriptOutputApiError && (isRevisionConflict(error) || isAmbiguousResponse(error))) {
        failures.push(`patch attempt ${attempt}: ${error.message}`);
        continue;
      }
      if (error instanceof ScriptOutputApiError) throw apiErrorToCli("PUBLISH BLOCKED: patch failed", error);
      throw error;
    }
  }
  throw outcomeUnknown(0, failures);
}

export async function commandCheckout(opts: Dict): Promise<[CommandOutput, number]> {
  const scriptPath = scriptJsonPath(opts);
  const discardLocal = Boolean(opts["discard_local"]);
  const paths = workingCopyPaths(scriptPath);
  const metadata = readWorkingCopyMetadata(scriptPath);
  const existed = fs.existsSync(scriptPath);
  const beforeFingerprint = localFileFingerprint(scriptPath);

  if (existed && metadata === null && !discardLocal) {
    throw new CliError("The target is an unmanaged local script.", {
            exitCode: EXIT_INPUT,
            errorCode: "LOCAL_TARGET_EXISTS",
            hint: `Run \`scriptctl checkout --script-path ${scriptPath} --discard-local\` only if the local file may be discarded.`,
            data: { actual: [scriptPath] },
          });
  }
  if (existed && metadata !== null && !discardLocal) {
    const currentHash = semanticScriptHash(readLocalScript(scriptPath));
    if (currentHash !== metadata.baseContentHash) {
      throw new CliError("The working copy has unpublished local changes.", {
              exitCode: EXIT_INPUT,
              errorCode: "WORKING_COPY_DIRTY",
              hint: "Publish the local changes, or pass --discard-local to replace them with the remote snapshot.",
              data: { actual: [scriptPath, `baseRevision=${metadata.baseRevision}`] },
            });
    }
  }

  const client = metadata !== null && !discardLocal
    ? clientForMetadata(opts, metadata)
    : remoteClient(configuredProjectGroupNo(opts) || null);
  let snapshot;
  try {
    snapshot = await client.getSnapshot();
  } catch (error) {
    if (error instanceof ScriptOutputApiError) {
      throw apiErrorToCli("CHECKOUT BLOCKED: snapshot query failed", error);
    }
    throw error;
  }
  if (snapshot === null || !isDict(snapshot.script)) {
    throw new CliError("No remote script_v2 snapshot exists for this project.", {
            exitCode: EXIT_INPUT,
            errorCode: "REMOTE_SCRIPT_NOT_FOUND",
            hint: "Use the ingest/create workflow and publish the first script instead of checkout.",
            data: { actual: [`projectGroupNo=${client.projectGroupNo}`] },
          });
  }
  assertLocalFileFingerprint(scriptPath, beforeFingerprint);
  const baseContentHash = semanticScriptHash(snapshot.script);
  atomicWriteJson(scriptPath, snapshot.script);
  writeWorkingCopyMetadata(scriptPath, {
    schemaVersion: 1,
    projectGroupNo: client.projectGroupNo,
    apiBaseUrl: client.gatewayBase,
    baseRevision: Number(snapshot.revision),
    baseContentHash,
  });
  const text = discardLocal && existed
    ? `Discarded the local file and checked out revision ${snapshot.revision} to ${scriptPath}`
    : `Checked out revision ${snapshot.revision} to ${scriptPath}`;
  return [{
    text,
    data: {
      changed: true,
      script_path: scriptPath,
      metadata_path: paths.metadataPath,
      managed: true,
      dirty: false,
      discarded_local: discardLocal && existed,
      project_group_no: client.projectGroupNo,
      base_revision: Number(snapshot.revision),
    },
  }, EXIT_OK];
}

export function commandWorkingCopyStatus(opts: Dict): [CommandOutput, number] {
  const scriptPath = scriptJsonPath(opts);
  const exists = fs.existsSync(scriptPath);
  const metadata = readWorkingCopyMetadata(scriptPath);
  const dirty = exists && metadata !== null
    ? semanticScriptHash(readLocalScript(scriptPath)) !== metadata.baseContentHash
    : null;
  const text = metadata === null
    ? `${scriptPath}: unmanaged local script`
    : `${scriptPath}: ${dirty ? "dirty" : "clean"} at revision ${metadata.baseRevision}`;
  return [{
    text,
    data: {
      script_path: scriptPath,
      exists,
      managed: metadata !== null,
      dirty,
      ...(metadata === null ? {} : {
        project_group_no: metadata.projectGroupNo,
        api_base_url: metadata.apiBaseUrl,
        base_revision: metadata.baseRevision,
      }),
    },
  }, EXIT_OK];
}

export async function commandWorkingCopyPublish(opts: Dict): Promise<[CommandOutput, number]> {
  const scriptPath = scriptJsonPath(opts);
  const script = readLocalScript(scriptPath);
  const scriptHash = semanticScriptHash(script);
  const metadata = readWorkingCopyMetadata(scriptPath);
  if (metadata !== null && metadata.baseContentHash === scriptHash) {
    return [{
      text: `No local changes; revision remains ${metadata.baseRevision}`,
      data: {
        changed: false,
        published: false,
        reason: "NO_CHANGES",
        revision: metadata.baseRevision,
        script_path: scriptPath,
      },
    }, EXIT_OK];
  }

  const validation = validateScriptReport(script);
  if (!Boolean(validation["passed"])) {
    throw new CliError("The local script does not pass its declared Script version validation.", {
            exitCode: EXIT_INPUT,
            errorCode: "PUBLISH_VALIDATION_FAILED",
            hint: `Run \`scriptctl validate --script-path ${scriptPath}\`, repair the issues, then publish again.`,
            data: { actual: (validation["issues"] as Dict[]).slice(0, 5).map((issue) => `${issue["path"] ?? "<root>"}: ${issue["summary"]}`) },
          });
  }
  const episodeCount = requireScriptDocument(script).episodes.length;

  const validationPayload: ValidationPayload = {
    status: "valid",
    summary: {
      passed: true,
      hasBlocking: false,
      stats: validation["stats"] ?? {},
      issueCount: 0,
    },
  };

  if (metadata !== null) {
    const client = clientForMetadata(opts, metadata);
    const result = await publishManagedLocalWins(
      client,
      scriptPath,
      script,
      scriptHash,
      metadata,
      validationPayload,
    );
    writeWorkingCopyMetadata(scriptPath, {
      schemaVersion: 1,
      projectGroupNo: client.projectGroupNo,
      apiBaseUrl: client.gatewayBase,
      baseRevision: result.revision,
      baseContentHash: scriptHash,
    });
    await syncEpisodeCount(client, episodeCount);
    return [{
      text: `Published local content over revision ${result.patchedFromRevision} → ${result.revision}`,
      data: {
        changed: true,
        published: true,
        created: false,
        transport: "patch",
        script_path: scriptPath,
        project_group_no: client.projectGroupNo,
        base_revision: result.patchedFromRevision,
        revision: result.revision,
        attempts: result.attempts,
        recovered: result.recovered,
      },
    }, EXIT_OK];
  }

  const client = remoteClient(configuredProjectGroupNo(opts) || null);
  const result = await publishInitial(client, scriptPath, script, scriptHash, validationPayload);
  writeWorkingCopyMetadata(scriptPath, {
    schemaVersion: 1,
    projectGroupNo: client.projectGroupNo,
    apiBaseUrl: client.gatewayBase,
    baseRevision: result.revision,
    baseContentHash: scriptHash,
  });
  await syncEpisodeCount(client, episodeCount);
  return [{
    text: `Published revision ${result.patchedFromRevision} → ${result.revision}`,
    data: {
      changed: true,
      published: true,
      created: true,
      transport: "patch",
      script_path: scriptPath,
      project_group_no: client.projectGroupNo,
      base_revision: result.patchedFromRevision,
      revision: result.revision,
      attempts: result.attempts,
      recovered: result.recovered,
    },
  }, EXIT_OK];
}
